import { MapOf } from 'flux-core';
import { DataType, IDataItem } from 'flux-definition';

type DataItems = MapOf<IDataItem<DataType>>;

/**
 * DiagramFactoryUtils
 * Contains factory functions which can be used to create new diagram or shape instances.
 * Using this we can perform shape updating logic outside the diagram locator class.
 */
export class DiagramFactoryUtils {

    /**
     * Build data items from the dataDef
     * "dataDef" is defined in the shape's def json file and it contains
     * common data required for data items which are not necessary to be stored in the db.
     * e.g.
     * dataDef: {
     *    isAbstract: {
     *      prop1: ..
     *      prop2: ..
     *    }
     * }
     *
     * data: {
     *    isAbstract: {}
     * }
     *
     * isAbstract props defined in the dataDef will be merged to isAbstract data item
     *
     *
     * "def" is defined to specify the key to lookup in the dataDef
     * e.g.
     * dataDef: {
     *    isAbstract: {
     *      prop1: ..
     *      prop2: ..
     *    }
     * }
     *
     * data: {
     *    whatEver: {
     *       def: "isAbstract"
     *    }
     * }
     *
     */
    public static createData( dataDef, data: DataItems ): DataItems {
        if ( dataDef && data ) {
            const resultingData = {};
            return new Proxy( data, {
                get: ( target: DataItems, key: string ) => {
                    if ( !target.hasOwnProperty( key )) {
                        return Reflect.get( target, key );
                    }
                    if ( !resultingData[ key ]) {
                        if ( key === 'description' && dataDef.description ) {
                            resultingData[key] = { ...dataDef.description, value: data[key].value };
                            return resultingData[ key ];
                        }
                        if ( dataDef[ key ]) {
                            resultingData[key] = Object.assign({}, dataDef[ key ], data[ key ]);
                        } else if ( dataDef[ data[ key ].def ]) {
                            resultingData[key] = Object.assign({}, dataDef[ data[ key ].def ], data[ key ]);
                        }
                        if (( resultingData[ key ] || data[key]).isNested && data[ key ].value ) {
                            resultingData[key] = resultingData[key] || Object.assign({}, data[ key ]);
                            resultingData[key].value = this.createData( dataDef, data[ key ].value as DataItems );
                        }
                        if ( !resultingData[key]) {
                            resultingData[key] = data[ key ];
                        }
                    }
                    return resultingData[ key ];
                },
            });
        }
        return data;
    }

}

